import { Apollo, gql } from 'apollo-angular';
import { AfterViewChecked, AfterViewInit, Component, ElementRef, Inject, LOCALE_ID, NgZone, OnInit, Optional, PLATFORM_ID, Renderer2, ViewChild } from '@angular/core';

import { Location, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { GameItem } from 'src/app/apollo/models/base-models';
import { catchError, filter, map, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { BehaviorSubject, EMPTY, Subject, combineLatest, defer, timer, zip } from 'rxjs';
import { BaseGamePage } from '../ase-game-page.component';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ToggleMenuBackService } from 'src/app/services/utils/toggle-menu-back.service';
import { gameInfoTrx, searchGameTrx } from 'src/app/router-translation.labels';
import { TranslationConfig } from 'src/app/utils/translate-config';
import { OpenDesktopDrawerService } from 'src/app/services/utils/open-desktop-drawer.service';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { DialogClosePreviousUrl } from 'src/app/services/utils/dialog-close-previouse-url.service';
import { MinimizeSiteService } from 'src/app/services/utils/hide-footer.service';
import { FilterGamesService } from 'src/app/services/utils/filter-games.service';
import { LICENCE } from 'src/app/global.tokens';
import { DetectDeviceService } from 'src/app/services/utils/detect-device.service';
import { FrontPageGamesService } from 'src/app/services/page/front-games.service';
import { LoginStatusService } from 'src/app/services/auth/login/login-status.service';
import { countryFromLocale } from 'src/app/utils/country-from-locale';
import { SearchGameService, SearchConfig, SearchDataItem, SearchConfigFilterItem, GameCategory } from 'src/app/services/games/search-game.service';
import { LicenceService } from 'src/app/services/utils/licence.service';


@Component({
  selector: 'app-search-game',
  templateUrl: './search-game.component.html',
  styleUrls: ['./search-game.component.scss']
})
export class SearchGameComponent extends BaseGamePage implements OnInit, AfterViewInit, AfterViewChecked {

  public searchConfig: SearchConfig = {
    searchFilters: SearchConfig.searchFilters.map(item => ({ ...item, data: [], activeFilters: 0 }))
  };

  public resultCount: number = 0;

  public searchTerm: string = '';
  public searchPlaceHolder: string = $localize`:@@search-game-placeholder:Search for a game...`;
  public filterItemName: string = '';

  public isLoggedIn: boolean = true;
  public isFilterSelect: boolean = false;
  public isHistory: boolean; //
  public isDesktop: boolean;
  public isBrowser: boolean = true;
  public isInputFocused: boolean = false;
  public isInputSign: boolean = false;

  public gameList: GameItem[] = [];
  public searchStatus: 'loading' | 'complete' | 'error' | 'short-term' = 'loading';
  public filtersLoaded = false;
  public categoryList: GameCategory[];

  private searchSubject = new BehaviorSubject<string>('');
  private filtersReady$ = new BehaviorSubject<boolean>(false);
  private retrySearch$ = new Subject<void>();
  private retryFilters$ = new Subject<void>();
  private hasInputChanged = false;
  private stopDialogScroll?: () => void;
  private unsubscribe$ = new Subject<void>();

  private restoreScrollTarget?: number;
  private restoreFrame?: number;
  private stopScrollRestore?: () => void;
  private preserveResultsOnRetry = false;
  private isOpenGame = false;



  constructor(
    private apollo: Apollo,
    public dialog: MatDialog,
    private toggleBackService: ToggleMenuBackService,
    protected translationConfig: TranslationConfig,
    private route: ActivatedRoute,
    private topDrawerService: OpenDesktopDrawerService,
    private router: Router,
    private location: Location,
    private previousUrl: DialogClosePreviousUrl,
    private minimizeSiteService: MinimizeSiteService,
    private filterGameService: FilterGamesService,
    public detectDeviceService: DetectDeviceService,
    private frontPageGamesService: FrontPageGamesService,
    private loginStatusService: LoginStatusService,
    private searchGamesService: SearchGameService,
    @Optional() public dialogRef: MatDialogRef<SearchGameComponent>,
    private elRef: ElementRef,
    private renderer: Renderer2,
    private ngZone: NgZone,
    @Inject(LOCALE_ID) public locale: string,
    @Inject(LICENCE) public licence: string,
    @Inject(PLATFORM_ID) public platformId: string,
    @Inject(MAT_DIALOG_DATA) @Optional() public dialogData: any,
    protected licenceService: LicenceService,
  ) {
    super(dialog);

    this.isDesktop = detectDeviceService.isDesktop();

    //localStorage?.clear()
  }

  @ViewChild('scrollContainer', { static: false }) scrollContainer!: ElementRef;

  ngOnInit(): void {

    if (!isPlatformBrowser(this.platformId)) return;
    this.onGlobalChange().pipe(takeUntil(this.unsubscribe$)).subscribe(progress => {
      this.gameList = progress.games;
      this.resultCount = progress.games.length;
      this.searchStatus = progress.complete ? 'complete' : 'loading';
    });

    this.retryFilters$.pipe(
      startWith(undefined),
      tap(() => {
        this.filtersLoaded = false;
        this.filtersReady$.next(false);
        this.clearResults('loading');
      }),
      switchMap(() => defer(() => this.loadInitialSearchData(this.searchConfig)).pipe(
        catchError(() => {
          this.clearResults('error');
          return EMPTY;
        })
      )),
      takeUntil(this.unsubscribe$)
    ).subscribe(([params, { searchConfig }]) => {
      this.setInitialSearchData(params, searchConfig, false);
      this.filtersLoaded = true;
      this.filtersReady$.next(true);
    });
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      if (!this.isDesktop) {
        const savedPosition = this.getScrollPosition();
        if (savedPosition > 0) {
          this.restoreScrollTarget = savedPosition;
          const startPosition = window.scrollY;
          const cancel = () => this.cancelScrollRestore();
          const onScroll = () => {
            if (window.scrollY !== startPosition) cancel();
          };
          const onKey = (event: KeyboardEvent) => {
            if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) cancel();
          };
          window.addEventListener('wheel', cancel, { passive: true });
          window.addEventListener('touchstart', cancel, { passive: true });
          window.addEventListener('scroll', onScroll, { passive: true });
          window.addEventListener('keydown', onKey);
          this.stopScrollRestore = () => {
            window.removeEventListener('wheel', cancel);
            window.removeEventListener('touchstart', cancel);
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('keydown', onKey);
          };
        }
      }
      this.checkDialog();
    }
  }

  ngAfterViewChecked(): void {
    if (this.restoreScrollTarget === undefined || this.restoreFrame !== undefined) return;
    // Wait for Angular to render enough progressive results before restoring.
    this.restoreFrame = this.ngZone.runOutsideAngular(() => requestAnimationFrame(() => {
      this.restoreFrame = undefined;
      const target = this.restoreScrollTarget;
      if (target === undefined) return;
      const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      if (maxScroll < target && this.searchStatus !== 'complete') return;
      this.cancelScrollRestore();
      window.scrollTo({ top: Math.min(target, maxScroll), behavior: 'auto' });
    }));
  }

  private cancelScrollRestore(): void {
    this.restoreScrollTarget = undefined;
    if (this.restoreFrame !== undefined) {
      cancelAnimationFrame(this.restoreFrame);
      this.restoreFrame = undefined;
    }
    this.stopScrollRestore?.();
    this.stopScrollRestore = undefined;
  }

  checkDialog() {
    if (this.dialogRef) {
      const dialogContent = this.scrollContainer.nativeElement.closest('.mat-dialog-container');
      //console.log('dialogContent', dialogContent);

      if (dialogContent) {
        this.stopDialogScroll = this.renderer.listen(dialogContent, 'scroll', () => {


          this.handleScroll(dialogContent);
        });

        //this.dialogRef?.backdropClick().subscribe(() => this.onCloseEvent());
      }

      if (this.dialogRef) {
        this.dialogRef.backdropClick().pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
          this.onCloseEvent();
        });
      }
    }
  }

  handleScroll(target: HTMLElement): void {
    const searchHead = target.querySelector('.search-head') as HTMLElement;
    const searchAction = target.querySelector('.search-action') as HTMLElement;
    const searchBody = target.querySelector('.search-body') as HTMLElement;
    //const searchFilterSheet = target.querySelector('.filter-sheet-wrapper') as HTMLElement;

    const dialogRect = target.getBoundingClientRect();
    const topPosition = dialogRect.top;// + 10;

    if (searchAction) {
      if (target.scrollTop > (searchHead?.offsetHeight ? searchHead.offsetHeight : 0)) {
        //console.log('target.scrollTop', target.scrollTop, 'searchHead?.offsetHeight', searchHead?.offsetHeight)
        searchAction.classList.add('fixed');

        this.renderer.setStyle(searchAction, 'top', `${topPosition}px`);
        this.renderer.setStyle(searchAction, 'padding-bottom', `16px`);
        this.renderer.setStyle(searchBody, 'padding-top', `${searchAction.offsetHeight}px`);
        this.renderer.setStyle(searchAction, 'width', `${target.offsetWidth - 32}px`);
      } else {
        searchAction.classList.remove('fixed');

        this.renderer.setStyle(searchBody, 'padding-top', `0px`);
      }

    }

  }

  onInputFocus(): void {
    this.isInputFocused = true;
    console.log('Input is focused');
  }

  onInputBlur(): void {
    this.isInputFocused = false;
    console.log('Input lost focus');
  }

  /* --------------------------------------------------------------------------------------------------- */

  setInitialSearchData(params: ParamMap, searchConfig: SearchConfig, isHistory: boolean) {
    const searchString = this.searchGamesService.getSearchHistory()
    const searchTerm = decodeURI(params.get('term') || '');
    this.isHistory = isHistory;

    this.searchTerm = this.hasInputChanged ? this.searchTerm : (searchTerm || searchString);
    this.isInputSign = this.searchTerm ? true : false;
    this.searchGamesService.setSearchFilters(searchConfig.searchFilters);
    this.searchSubject.next(this.searchTerm);

  }

  loadInitialSearchData(searchConfig: SearchConfig) {
    return zip(
      this.route.paramMap.pipe(take(1)),
      this.searchGamesService.getFilters().pipe(
        map((filters) => this.searchGamesService.setFilters(filters, searchConfig)),
        map((filters) => this.searchGamesService.setHistory(searchConfig)),
      )
    );
  }

  /* --------------------------------------------------------------------------------------------------- */

  onOpenGame(gameName: string) {
    this.setScrollPosition(isPlatformBrowser(this.platformId) ? window.scrollY : 0);
    this.topDrawerService.close();

    if (this.dialogRef) {
      this.dialogRef.close('game-opened');
    }

    this.isOpenGame = true;

    const urlGameName = gameName;
    this.previousUrl.setUrl(this.location.path().split('/').filter(item => item !== ''));
    this.router.navigate(["", this.translationConfig.getTranslation(gameInfoTrx), urlGameName]);
  }

  onCloseEvent() {
    if (this.dialogRef) {
      this.dialogRef.close();
    }

    this.router.navigate(['/']);
  }

  onFilterSheetClose() {
    this.isFilterSelect = false;
    this.filterItemName = '';

    if (this.isDesktop) {
      setTimeout(() => {
        const dialogContent = this.scrollContainer.nativeElement.closest('.mat-dialog-container');
        dialogContent.scrollTo({ top: 0, behavior: 'smooth' });

        const searchHead = dialogContent.querySelector('.search-head') as HTMLElement;
        const searchAction = dialogContent.querySelector('.search-action') as HTMLElement;
        const searchBody = dialogContent.querySelector('.search-body') as HTMLElement;

        if (searchAction && searchHead && searchBody) {
          //this.renderer.addClass(searchAction, 'fixed');
          //this.renderer.setStyle(searchAction, 'top', `97px`);
          //this.renderer.setStyle(searchAction, 'padding-bottom', `16px`);
          //this.renderer.setStyle(searchBody, 'padding-top', `${searchAction.offsetHeight - searchHead.offsetHeight}px`);
        } else {
          console.error('One or more elements are not found.');
        }
      }, 0);
    } else {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }

  onFilterChoose(filterItem: SearchConfigFilterItem) {
    if (!this.filtersLoaded) return;
    this.cancelScrollRestore();
    this.isFilterSelect = true;
    this.filterItemName = filterItem.name;
  }

  onSearchChange(searchString: string) {
    this.cancelScrollRestore();
    this.hasInputChanged = true;
    searchString ? this.isInputSign = true : this.isInputSign = false;
    this.searchTerm = searchString;
    //console.log('onSearchChange', searchString, this.isInputSign)

    this.searchSubject.next(searchString);
    this.searchGamesService.saveSearchHistory(searchString);
  }

  onGlobalChange() {
    return combineLatest([
      this.searchGamesService.searchFilters$,
      this.searchSubject,
      this.filtersReady$,
      this.retrySearch$.pipe(startWith(undefined)),
    ]).pipe(
      // Switch before debounce: every keystroke cancels the previous request, even a short term.
      switchMap(([filterItems, searchString, ready]) => {
        const preserveResults = this.preserveResultsOnRetry;
        this.preserveResultsOnRetry = false;
        if (!ready) return EMPTY;
        if (!this.isDesktop) this.location.replaceState(searchGameTrx + '/' + searchString);
        if (searchString.length > 0 && searchString.length < 4) {
          this.clearResults('short-term');
          return EMPTY;
        }
        const retainedGames = preserveResults ? this.gameList : [];
        if (preserveResults) this.searchStatus = 'loading';
        else this.clearResults('loading');
        return timer(300).pipe(
          switchMap(() => defer(() => this.searchGames(searchString, filterItems))),
          map(progress => progress.complete || !retainedGames.length ? progress : {
            ...progress,
            games: Array.from(new Map(
              [...retainedGames, ...progress.games].map(game => [game.gameid, game])
            ).values())
          }),
          catchError(() => {
            // Keep games already received; their count is explicitly marked as incomplete.
            this.searchStatus = 'error';
            return EMPTY;
          })
        );
      })
    );
  }

  /* --------------------------------------------------------------------------------------------------- */

  searchGames(searchString: string, filterItems: SearchConfigFilterItem[]) {
    return this.searchGamesService.searchProgress(searchString, filterItems);
  }

  retrySearch(): void {
    if (this.filtersLoaded) {
      this.preserveResultsOnRetry = true;
      this.retrySearch$.next();
    }
    else this.retryFilters$.next();
  }

  private clearResults(status: typeof this.searchStatus): void {
    this.gameList = [];
    this.resultCount = 0;
    this.searchStatus = status;
  }

  trackGame = (_index: number, game: GameItem) => game.gameid;

  setScrollPosition(scrollPosition: number) {
    //console.log(`*set this._scrollPosition`, scrollPosition);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(this.searchGamesService.scrollPositionKey, scrollPosition.toString());
    }
  }
  
  getScrollPosition(): number {
    if (isPlatformBrowser(this.platformId)) {
      const saved = localStorage.getItem(this.searchGamesService.scrollPositionKey);
      let scrollPosition = saved ? parseInt(saved, 10) : 0;
      //console.log(`*get this._scrollPosition`, scrollPosition);
      return scrollPosition;
    } else {
      return 0
    }
  }

  ngOnDestroy() {
    this.stopDialogScroll?.();
    this.cancelScrollRestore();
    // this.searchGamesService.saveSearchHistory('');
    // if (isPlatformBrowser(this.platformId)) {
    //   localStorage?.removeItem(this.searchGamesService.primeCategoryKey);
    // }

    // this.searchConfig.searchFilters.forEach((filterConfig) => {
    //   if (isPlatformBrowser(this.platformId)) {
    //     localStorage?.removeItem(filterConfig.storageKey);
    //   }
    // });

    if ((!this.isOpenGame && !this.isDesktop) || this.isDesktop) {
      this.setScrollPosition(0);
      this.searchGamesService.saveSearchHistory('');
      if (isPlatformBrowser(this.platformId)) {
        localStorage?.removeItem(this.searchGamesService.primeCategoryKey);
      }
  
      this.searchConfig.searchFilters.forEach((filterConfig) => {
        if (isPlatformBrowser(this.platformId)) {
          localStorage?.removeItem(filterConfig.storageKey);
        }
      });
    }

    this.filtersReady$.complete();
    this.searchSubject.complete();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();

  }

  /* --------------------------------------------------------------------------------------------------- */

}
